import React, { useCallback, useState } from "react";
import { Modal, Button, Form, Input } from "antd";
import { useTranslation } from "react-i18next";
import axios, { AxiosError, AxiosResponse } from "axios";
import * as Yup from "yup";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import api from "../../../services/api";
import { IActionModalProps } from "..";
import { useToast } from "../../../hooks/toast";
import FormItem from "../../FormItem";
import getValidationErrors from "../../../utils/getValidationErrors";

const { confirm } = Modal;

interface IApproveModalFormData {
  purchase_order: string;
  identifier: string;
}

interface IApprovalData {
  approved: boolean;
  purchase_order?: string;
}

const layout = {
  labelCol: { span: 8 },
  wrapperCol: { span: 16 },
};

const ApprovalModal: React.FC<IActionModalProps> = ({
  project,
  handleUpdateProject,
}) => {
  const { t } = useTranslation(["project-actions"]);
  const [visible, setVisible] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [validationErrors, setValidationErrors] = useState({});

  const [form] = Form.useForm();

  const { addToast } = useToast();

  const handleClose = useCallback(() => {
    setVisible(false);
  }, []);

  const generateErrorToast = useCallback(
    (response: AxiosResponse | undefined) => {
      let description;

      switch (response?.status) {
        case 405:
          description =
            "O projeto tem que estar na fase de aguardo de aprovação para ser aprovado";
          break;
        case 404:
          description = "O projeto não foi encontrado";
          break;
        default:
          description = "Um erro occoreu ao aprovar o projeto, tente novamente";
      }

      addToast({
        type: "error",
        title: "Erro ao estimar",
        description,
      });
    },
    [addToast]
  );

  const handleSendRequest = useCallback(
    async (payload: IApprovalData) => {
      try {
        setLoading(true);

        const response = await api.patch(
          `/projects/${project.id}/approve-disapprove`,
          payload
        );

        handleUpdateProject(response.data);
        addToast({
          type: "success",
          title: "Projeto aprovado",
          description: "O projeto foi aprovado com sucesso",
        });
      } catch (err) {
        if (axios.isAxiosError(err)) {
          const serverError = err as AxiosError;
          generateErrorToast(serverError.response);
        } else {
          console.log(err);
        }
      } finally {
        setLoading(false);
      }
    },
    [addToast, generateErrorToast, handleUpdateProject, project.id]
  );

  const handleSubmit = useCallback(
    async (data: IApproveModalFormData) => {
      try {
        const schema = Yup.object().shape({
          purchase_order: Yup.string().required(
            "Ordem de compra obrigatória para esse cliente"
          ),
          identifier: Yup.string().required(
            "Identificador obrigatório nessa etapa do projeto"
          ),
        });

        await schema.validate(data, {
          abortEarly: false,
        });

        const { purchase_order, identifier } = data;

        const payload = {
          approved: true,
          purchase_order,
          identifier,
        };

        handleSendRequest(payload);
      } catch (err) {
        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrors(err);
          setValidationErrors(errors);
        }
      }
    },
    [handleSendRequest]
  );

  const showConfirm = useCallback((): void => {
    confirm({
      title: "Esse projeto foi aprovado ?",
      icon: <ExclamationCircleOutlined />,
      content: 'Se confirmar, o projeto irá passar na fase "Aprovado" ',
      onOk() {
        handleSendRequest({
          approved: true,
        });
      },
      okButtonProps: {
        loading,
      },
    });
  }, [handleSendRequest, loading]);

  const handleActionOnButtonClick = useCallback(() => {
    if (project.customer?.generate_purchase_order) {
      setVisible((oldVisible) => !oldVisible);
    } else {
      showConfirm();
    }
  }, [project.customer?.generate_purchase_order, showConfirm]);

  return (
    <>
      <Modal
        title={t("approve")}
        visible={visible}
        width={600}
        onOk={() => form.submit()}
        onCancel={() => handleClose()}
        okButtonProps={{ htmlType: "submit", loading }}
        getContainer={false}
        destroyOnClose
      >
        <Form
          {...layout}
          form={form}
          name="approve-form"
          onFinish={handleSubmit}
        >
          <FormItem
            name="purchase_order"
            required
            label={t("purchase_order")}
            errors={validationErrors}
          >
            <Input />
          </FormItem>
          <FormItem
            name="identifier"
            required
            initialValue={project.identifier}
            label={t("identifier")}
            errors={validationErrors}
          >
            <Input />
          </FormItem>
        </Form>
      </Modal>
      <Button type="primary" onClick={handleActionOnButtonClick}>
        {t("approve")}
      </Button>
    </>
  );
};

export default ApprovalModal;
